/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	Directive,
	Input,
	Output,
	TemplateRef,
	ViewContainerRef,
	OnInit,
	EventEmitter,
	OnChanges,
	SimpleChanges,
} from '@angular/core';

import { LoadingBlockerSpinnerComponent } from './loading-blocker-spinner/loading-blocker-spinner.component';
import { LoadingBlockerSuccessComponent } from './loading-blocker-success/loading-blocker-success.component';
import { LoadingBlockerFailureComponent } from './loading-blocker-failure/loading-blocker-failure.component';

export enum LoadingBlockerState {
	empty = 'empty',
	complete = 'complete',
	inProgress = 'inProgress',
	failure = 'failure',
}

export const LOADING_BLOCKER_ANIMATION_DELAY = 900;

class ViewRefManager {
	constructor(private viewContainer: ViewContainerRef) {}
	private cache: any = {};

	public attach(component, type: LoadingBlockerState, inputs?: { [key: string]: any }) {
		if (this.cache[type]) {
			return this.viewContainer.insert(this.cache[type].hostView);
		}

		const viewRef = this.viewContainer.createComponent(component);

		if (inputs) {
			Object.keys(inputs).forEach((key) => {
				viewRef.setInput(key, inputs[key]);
			});
		}

		this.cache[type] = viewRef;
		return viewRef;
	}
	public detach() {
		if (!this.viewContainer.length) {
			return;
		}

		Object.keys(this.cache).forEach((key) => {
			const index = this.viewContainer.indexOf(this.cache[key].hostView);
			if (index !== -1) {
				this.viewContainer.detach(index);
			}
		});
	}
}

@Directive({
	selector: '[loadingBlocker]',
})
export class LoadingBlockerDirective implements OnInit, OnChanges {
	private viewRefManager = new ViewRefManager(this.viewContainer);
	private _animationDelay = LOADING_BLOCKER_ANIMATION_DELAY;
	private _progressInput = LoadingBlockerState.empty;
	private _initialized = false;

	//consider doing both a complete callback and a change callback, this one is just complete
	@Output() public onStateChange = new EventEmitter<LoadingBlockerState>();

	@Input() set animationDelay(delay: number) {
		this._animationDelay = delay;
	}

	/**
	 * Displaying Locally is the default, but to get it works you should set the parent element
	 * to a position that works for this purpose (relative | fixed | absolute | sticky)
	 */
	@Input() displayGlobally: boolean = false;

	@Input() transparent: boolean = true;

	@Input('loadingBlocker') progressInput: LoadingBlockerState | boolean = LoadingBlockerState.empty;

	@Input() customContent?: TemplateRef<any>;

	public handleInput(state: LoadingBlockerState) {
		if (state === this._progressInput) {
			return;
		}
		this.viewRefManager.detach();

		switch (state) {
			case LoadingBlockerState.inProgress:
				this.viewRefManager.attach(LoadingBlockerSpinnerComponent, state, {
					transparent: this.transparent,
					displayGlobally: this.displayGlobally,
					customContent: this.customContent,
				});
				break;
			case LoadingBlockerState.complete:
				this.viewRefManager.attach(LoadingBlockerSuccessComponent, state, {
					displayGlobally: this.displayGlobally,
				});
				break;
			case LoadingBlockerState.failure:
				this.viewRefManager.attach(LoadingBlockerFailureComponent, state, {
					displayGlobally: this.displayGlobally,
				});
				break;
			default:
				break;
		}

		if (state === LoadingBlockerState.complete || state === LoadingBlockerState.failure) {
			setTimeout(() => {
				this.onStateChange.emit(state);
				this._progressInput = LoadingBlockerState.empty;
				this.viewRefManager.detach();
			}, this._animationDelay);
		}

		this._progressInput = state;
	}

	private mapLoadingBlockerStateValue(value: LoadingBlockerState | boolean): LoadingBlockerState {
		if (typeof value === 'boolean') {
			return value ? LoadingBlockerState.inProgress : LoadingBlockerState.empty;
		}
		return value;
	}

	constructor(private templateRef: TemplateRef<any>, private viewContainer: ViewContainerRef) {}

	public ngOnInit() {
		this.viewContainer.createEmbeddedView(this.templateRef);
		this._initialized = true;
		// run once with whatever inputs we already have
		this.handleInput(this.mapLoadingBlockerStateValue(this.progressInput));
	}

	public ngOnChanges(changes: SimpleChanges): void {
		// wait until template is created
		if (!this._initialized) {
			return;
		}

		if (
			changes['progressInput'] ||
			changes['loadingBlocker'] ||
			changes['transparent'] ||
			changes['displayGlobally'] ||
			changes['customContent']
		) {
			const state = this.mapLoadingBlockerStateValue(changes['progressInput'].currentValue);
			this.handleInput(state);
		}
	}
}

// loadingBlocker functionality
// - is placed on a wrapper component
// - should slap a div that fully overlaps the screen or just a component (depending on displayGlobally Input)
// --- for displayLocally, you should set the parent element to a position that works for this purpose (relative | fixed | absolute | sticky)
//   it's added to when a condition is true, and make it go away when a condition is false
// --- only complete is relevant for showing/ hiding the blocker
// --- success and failure should simply show alternative ui and include animation time in the blocker going away
// transparent input will make the blocker transparent and show the background content.
